import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { SG_APV3_CREATE_CAMPAIGN, SG_APV3_GET_CAMPAIGNS, SG_APV3_GET_AVAILABLE_DEBRIEF_REPORTS } from "constants/actions";
import NewCampaignModal from "./NewCampaignModal";
import CampaignsTable from "./CampaignsTable";
import ActionPlanBreadcrumb from "./ActionPlanBreadcrumb";
import { useNavigate } from "react-router-dom";
import { useToasts } from 'react-toast-notifications';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Icon component for Action Planning
const ActionPlanList = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12L9 12M21 6L9 6M21 18L9 18M5 12C5 12.5523 4.55228 13 4 13C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11C4.55228 11 5 11.4477 5 12ZM5 6C5 6.55228 4.55228 7 4 7C3.44772 7 3 6.55228 3 6C3 5.44772 3.44772 5 4 5C4.55228 5 5 5.44772 5 6ZM5 18C5 18.5523 4.55228 19 4 19C3.44772 19 3 18.5523 3 18C3 17.4477 3.44772 17 4 17C4.55228 17 5 17.4477 5 18Z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Page = styled.main`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 20px;
  max-width: 1100px;
  margin: 0 auto;
  min-height: 100vh;
  animation: ${fadeUp} 0.6s ease-out;
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 28px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Panel = styled.div`
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  background: #ffffff;
  padding: 16px;
`;

const PanelTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 18px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
`;

const ListItem = styled.li`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  padding: 10px 12px;
`;

const Small = styled.div`
  font-size: 13px; color: #5a5a5a;
`;

const Button = styled.button`
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
`;

function toIsoOrNull(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    return d.toISOString();
  } catch (e) { return null; }
}

const ActionPlanV3 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const campaigns = useSelector((s) => s.action_planning_v3?.campaigns);
  const loading = useSelector((s) => s.action_planning_v3?.loading);
  const availableDebriefReports = useSelector((s) => s.action_planning_v3?.availableDebriefReports || []);
  const error = useSelector((s) => s.action_planning_v3?.error);
  const lastCreated = useSelector((s) => s.action_planning_v3?.lastCreatedCampaign);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Temporarily set to 3 for testing - change back to 10 after
  const [pageInputValue, setPageInputValue] = useState("1");

  useEffect(() => {
    dispatch({ type: SG_APV3_GET_CAMPAIGNS });
  }, [dispatch]);

  // Preload available debrief report templates for campaign creation
  useEffect(() => {
    dispatch({ type: SG_APV3_GET_AVAILABLE_DEBRIEF_REPORTS });
  }, [dispatch]);

  // Reset page when campaigns change
  useEffect(() => {
    setCurrentPage(1);
    setPageInputValue("1");
  }, [campaigns?.results?.length]);

  // After create, close modal and refresh
  useEffect(() => {
    if (lastCreated?.id) {
      setShowCreateModal(false);
      dispatch({ type: SG_APV3_GET_CAMPAIGNS });
      addToast('Campaign successfully created', {
        appearance: 'success',
        autoDismiss: true,
      });
    }
  }, [lastCreated, dispatch, addToast]);

  // Fallback close/reset if create completed successfully but no id was returned
  useEffect(() => {
    if (creating && !loading) {
      if (!error) {
        setShowCreateModal(false);
        dispatch({ type: SG_APV3_GET_CAMPAIGNS });
        addToast('Campaign successfully created', {
          appearance: 'success',
          autoDismiss: true,
        });
      }
      setCreating(false);
    }
  }, [creating, loading, error, dispatch, addToast]);

  const campaignItems = useMemo(() => campaigns?.results || [], [campaigns]);

  // Pagination logic
  const totalPages = Math.ceil(campaignItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCampaigns = campaignItems.slice(startIndex, endIndex);

  // Sync page input value with current page when it changes externally
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Handle page jump input
  const handlePageJump = (e) => {
    if (e.key === "Enter") {
      const pageNumber = parseInt(pageInputValue, 10);
      if (
        !isNaN(pageNumber) &&
        pageNumber >= 1 &&
        pageNumber <= totalPages
      ) {
        setCurrentPage(pageNumber);
      } else {
        // Reset to current page if invalid
        setPageInputValue(currentPage.toString());
      }
    }
  };

  // Handle blur - reset to current page if invalid
  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      setPageInputValue(currentPage.toString());
    }
  };

  const handleCreateSubmit = (form) => {
    setCreating(true);
    const payload = {
      name: form.name,
      description: form.description,
      start_date: toIsoOrNull(form.start_date),
      end_date: toIsoOrNull(form.end_date),
      debrief_report_id: form.debrief_report_id ? Number(form.debrief_report_id) : undefined,
    };
    dispatch({ type: SG_APV3_CREATE_CAMPAIGN, payload });
  };

  return (
    <Page>
      <ActionPlanBreadcrumb />
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <ActionPlanList />
          </IconWrapper>
          <HeaderText>
            <Title>Action Planning</Title>
            <Subtitle>Manage action planning campaigns</Subtitle>
          </HeaderText>
        </HeaderContent>

        <ButtonGroup>
          <Button type="button" onClick={() => setShowCreateModal(true)}>New Campaign</Button>
        </ButtonGroup>
      </HeaderSection>

      <CampaignsTable
        campaigns={currentCampaigns}
        totalPages={totalPages}
        currentPage={currentPage}
        pageInputValue={pageInputValue}
        onPageChange={(page) => setCurrentPage(page)}
        onPageInputChange={setPageInputValue}
        onPageJump={handlePageJump}
        onPageInputBlur={handlePageInputBlur}
      />

      <NewCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        loading={loading}
        templates={availableDebriefReports}
      />
    </Page>
  );
};

export default ActionPlanV3;


